import React, { useState } from 'react'
import { Form, Button, Modal, Alert, Card, Spinner } from 'react-bootstrap'
import { FaCheckCircle, FaKey } from 'react-icons/fa'
import useFetch from '../../_fetch'
import { useAuth } from '../../auth/auth-context'

export default function PasswordSection(opts: {
    setSuccessModalMsg: (value: React.SetStateAction<string>) => void
    identityLabel?: string
    isEmailIdentity?: boolean
}) {
    const { doFetch } = useFetch()
    const { login } = useAuth()
    const [showModal, setShowModal] = useState(false)
    const [submitting, setSubmitting] = useState(false) // State to track form submission
    const [passwordFields, setPasswordFields] = useState({
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    })
    const [passwordError, setPasswordError] = useState('')

    const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setPasswordFields((prevFields) => ({
            ...prevFields,
            [name]: value
        }))
    }

    const handlePasswordUpdate = async (e: React.FormEvent) => {
        e.preventDefault()
        if (passwordFields.newPassword !== passwordFields.confirmNewPassword) {
            setPasswordError('Passwords do not match')
        } else {
            setPasswordError('')
            setSubmitting(true) 
            
            try {
                const res = await doFetch('/update-password', {
                    oldPassword: passwordFields.oldPassword,
                    newPassword: passwordFields.newPassword
                })

                if (res.status >= 400) {
                    setPasswordError(await res.text())
                } else {
                    const { token } = await res.json()
                    login(token)
                    setShowModal(false)
                    setPasswordFields({
                        oldPassword: '',
                        newPassword: '',
                        confirmNewPassword: ''
                    })
                    opts.setSuccessModalMsg('Password updated successfully')
                }
            } catch (error) {
                setPasswordError('Failed to update password')
            } finally {
                setSubmitting(false) 
            }
        }
    }

    const handleCloseModal = () => {
        setPasswordError('')
        setShowModal(false)
        setPasswordFields({
            oldPassword: '',
            newPassword: '',
            confirmNewPassword: ''
        })
    }

    return (
        <>
            <Card className="user-settings-panel border-0">
                <Card.Header className="bg-white border-0 user-settings-card-header">
                    <div className="user-settings-eyebrow">{opts.isEmailIdentity ? 'Personal settings' : 'Security'}</div>
                    <h5 className="mb-1">Password and sign-in</h5>
                    <div className="text-muted small">
                        {opts.isEmailIdentity
                            ? <>Manage the personal login for <strong>{opts.identityLabel}</strong>. This password is used across every account you can access.</>
                            : <>Manage the credentials used to sign in to <strong>{opts.identityLabel || 'this account'}</strong>.</>}
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="user-settings-security-panel">
                        <div className="user-settings-security-icon" aria-hidden="true"><FaKey /></div>
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 fw-semibold">
                                Password protection
                                <span className="user-settings-security-status"><FaCheckCircle aria-hidden="true" /> Active</span>
                            </div>
                            <div className="text-muted small mt-1">Use a unique password with at least 8 characters and change it if you suspect unauthorized access.</div>
                        </div>
                        <Button variant="outline-primary" className="flex-shrink-0" onClick={() => setShowModal(true)}>
                            Change password
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title as="h5">Change password</Modal.Title>
                        <div className="text-muted small mt-1">Your new password must contain at least 8 characters.</div>
                    </div>
                </Modal.Header>
                <Form onSubmit={handlePasswordUpdate}>
                    <Modal.Body>
                        <Form.Group controlId="formOldPassword" className="mb-3">
                            <Form.Label>Current password</Form.Label>
                            <Form.Control
                                type="password"
                                autoComplete="current-password"
                                placeholder="Enter current password"
                                name="oldPassword"
                                value={passwordFields.oldPassword}
                                onChange={handlePasswordInputChange}
                                required
                                minLength={8}
                            />
                        </Form.Group>
                        <Form.Group controlId="formNewPassword" className="mb-3">
                            <Form.Label>New password</Form.Label>
                            <Form.Control
                                type="password"
                                autoComplete="new-password"
                                placeholder="Enter new password"
                                name="newPassword"
                                value={passwordFields.newPassword}
                                onChange={handlePasswordInputChange}
                                required
                                minLength={8}
                            />
                        </Form.Group>
                        <Form.Group controlId="formConfirmNewPassword">
                            <Form.Label>Confirm new password</Form.Label>
                            <Form.Control
                                type="password"
                                autoComplete="new-password"
                                placeholder="Confirm new password"
                                name="confirmNewPassword"
                                value={passwordFields.confirmNewPassword}
                                onChange={handlePasswordInputChange}
                                required
                                minLength={8}
                            />
                            {passwordError && <Alert className="mt-3" variant="danger">{passwordError}</Alert>}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? (
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            ) : (
                                'Update password'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    )
}
